
import { useState } from 'react';
import { useReports } from './hooks/useReports';
import { exportPDF } from './lib/pdfExport';

export default function MonthlyFinanceReports() {
  const { finance } = useReports();
  const [period, setPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    reportType: 'summary'
  });
  
  const q = finance(period);
  
  if (q.isLoading) return <div className="p-6">Loading...</div>;
  
  const data = q.data || {};
  const rows = data.summary || [];
  
  const handleExportPDF = () => {
    const headers = ['Month', 'Revenue', 'Expenses', 'Profit'];
    const reportData = rows.map((r: any) => [r.month, r.revenue, r.expenses, r.profit]);
    exportPDF('Monthly Finance Report', headers, reportData);
  };
  
  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Monthly Finance Reports</h1>
        <button 
          onClick={handleExportPDF} 
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-primary)]/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          Export PDF
        </button>
      </div>
      
      {/* Period Selection */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold mb-4">Report Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Month</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.month}
              onChange={e => setPeriod({...period, month: parseInt(e.target.value)})}
            >
              {Array.from({length: 12}, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Year</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.year}
              onChange={e => setPeriod({...period, year: parseInt(e.target.value)})}
            >
              {Array.from({length: 5}, (_, i) => (
                <option key={i} value={new Date().getFullYear() - i}>
                  {new Date().getFullYear() - i}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Report Type</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.reportType}
              onChange={e => setPeriod({...period, reportType: e.target.value})}
            >
              <option value="summary">Financial Summary</option>
              <option value="detailed">Detailed P&L</option>
              <option value="cashflow">Cash Flow</option>
              <option value="balance">Balance Sheet</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Key Financial Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
              <p className="text-2xl font-bold text-[var(--color-primary)]">
                ₹{data.revenue?.toLocaleString() || 0}
              </p>
              <p className={`text-sm ${data.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.revenueGrowth >= 0 ? '+' : ''}{data.revenueGrowth || 0}% from last month
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-blue-600"></i>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Expenses</h3>
              <p className="text-2xl font-bold text-red-600">
                ₹{data.expenses?.toLocaleString() || 0}
              </p>
              <p className={`text-sm ${data.expenseGrowth <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.expenseGrowth >= 0 ? '+' : ''}{data.expenseGrowth || 0}% from last month
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-red-600"></i>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Net Profit</h3>
              <p className={`text-2xl font-bold ${(data.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ₹{data.profit?.toLocaleString() || 0}
              </p>
              <p className={`text-sm ${data.profitGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.profitGrowth >= 0 ? '+' : ''}{data.profitGrowth || 0}% from last month
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-line-chart-line text-xl text-green-600"></i>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Profit Margin</h3>
              <p className="text-2xl font-bold text-[var(--color-accent)]">
                {data.revenue ? ((data.profit / data.revenue) * 100).toFixed(1) : 0}%
              </p>
              <p className="text-sm text-gray-500">
                Target: 15%
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i className="ri-percent-line text-xl text-purple-600"></i>
            </div>
          </div>
        </div>
      </div>
      
      {/* Monthly Finance Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Monthly Financial Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Profit</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Margin %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r: any, index: number) => (
                <tr key={r.month || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.month}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{r.revenue?.toLocaleString() || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{r.expenses?.toLocaleString() || 0}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                    (r.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ₹{r.profit?.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {r.revenue ? ((r.profit / r.revenue) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No financial data available for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
